import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Play, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/components/ui/LanguageContext';
import { motion } from 'framer-motion';
import { TAG_FILTER_DICTIONARY, getDictionaryLabel } from '@/config/catalogFilters';

const watchedStampSrc = '/sityouzumi-v1.png';

function appendReturnTo(target, returnTo) {
  const separator = target.includes('?') ? '&' : '?';
  return `${target}${separator}returnTo=${encodeURIComponent(returnTo)}`;
}

export default function VideoCard({ video, progress, showSeriesPosition = false }) {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [stampFailed, setStampFailed] = useState(false);
  
  const progressPercent = progress 
    ? Math.round((progress.progress_seconds / (video.duration_minutes * 60)) * 100)
    : 0;
  const isCompleted = progress?.is_completed === true || video.user_progress?.is_completed === true;

  const genreLabels = {
    programming: t('programming'),
    design: t('design'),
    business: t('business'),
    language: t('language'),
    other: t('other')
  };
  const tagSlugs = (Array.isArray(video.tags) ? video.tags : [])
    .map((entry) => (typeof entry === 'string' ? entry : entry?.slug))
    .filter(Boolean);
  const displayTagSlugs = tagSlugs.slice(0, 5);
  const hiddenTagCount = Math.max(0, tagSlugs.length - displayTagSlugs.length);

  const durationLabel = (() => {
    const seconds = Number(video.duration_seconds || 0);
    if (seconds > 0) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;

      if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
      }

      return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
    }

    if (video.duration_minutes > 0) {
      return `${video.duration_minutes} ${t('minutes')}`;
    }

    return '--:--';
  })();

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Link
        to={appendReturnTo(createPageUrl(`VideoPlayer?id=${video.id}`), `${location.pathname}${location.search}`)}
        state={{ returnTo: `${location.pathname}${location.search}` }}
      >
        <div className="group relative bg-white rounded-xl overflow-hidden hover:bg-[#faf6f1] transition-all duration-300">
          {/* Thumbnail */}
          <div className="relative aspect-video overflow-hidden">
            {video.thumbnail_url && !thumbnailFailed ? (
              <img
                src={video.thumbnail_url}
                alt={video.title}
                className={`w-full h-full object-cover transition-all duration-500 ${
                  isCompleted ? '' : 'group-hover:scale-105'
                }`}
                onError={() => setThumbnailFailed(true)}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#f0e3d3] to-[#d6c0a0] flex items-center justify-center text-[#6b5540] text-sm font-medium">
                No Thumbnail
              </div>
            )}

            {isCompleted && !stampFailed && (
              <img
                src={watchedStampSrc}
                alt="Watched"
                className="absolute left-1 top-1 z-10 w-[calc(33.333%-20px)] min-w-12 max-w-20 pointer-events-none drop-shadow-[0_3px_8px_rgba(61,40,23,0.25)]"
                onError={() => setStampFailed(true)}
              />
            )}
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Play className="w-6 h-6 text-white fill-white ml-1" />
              </div>
            </div>

            {/* Duration Badge */}
            <div className="absolute bottom-3 right-3 bg-[#3d2817]/90 px-2 py-1 rounded text-xs text-[#f5ede3] font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {durationLabel}
            </div>

            {/* Free Badge */}
            {video.is_free && (
              <Badge className={`absolute left-3 bg-[#8b6f47] text-[#f5ede3] ${isCompleted ? 'bottom-3' : 'top-3'}`}>
                FREE
              </Badge>
            )}

            {/* Series Position */}
            {showSeriesPosition && video.order_in_series && (
              <Badge className="absolute top-3 right-3 bg-[#3d2817]/90 text-[#f5ede3]">
                {t('episode')}{video.order_in_series}
              </Badge>
            )}

            {/* Progress Bar */}
            {progressPercent > 0 && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#d4c4b0]">
                <div 
                  className="h-full bg-[#8b6f47]"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-4 space-y-2">
            <h3 className="font-semibold text-[#2d1810] text-sm line-clamp-2 group-hover:text-[#8b6f47] transition-colors">
              {video.title}
            </h3>
            {displayTagSlugs.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {displayTagSlugs.map((slug) => (
                  <Badge key={slug} variant="outline" className="text-[10px] text-[#5d4529] border-[#d4c4b0]">
                    {getDictionaryLabel(TAG_FILTER_DICTIONARY, slug, language, slug)}
                  </Badge>
                ))}
                {hiddenTagCount > 0 && (
                  <Badge variant="outline" className="text-[10px] text-[#5d4529] border-[#d4c4b0]">
                    +{hiddenTagCount}
                  </Badge>
                )}
              </div>
            )}
            
            <div className="flex items-center justify-between">
              {video.genre ? (
                <Badge variant="outline" className="text-xs text-[#5d4529]">
                  {genreLabels[video.genre] || video.genre}
                </Badge>
              ) : (
                <span />
              )}
              
              <span />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}